import React from 'react'

import { Icon } from '@clientbase/clientbase-library'
import { Box, Stack, Tooltip, Typography } from '@mui/material'

import InfoHint from 'components/InfoHint'

import { theme } from 'styles/theme'

type CelulaProps = {
  label: string
  children: React.ReactNode
  /**
   * Ocupa a linha inteira da grade. Para o que é naturalmente comprido —
   * meios de pagamento, tags, descrição —, que não cabe numa coluna de 140px.
   */
  larga?: boolean
  /**
   * Dado de apoio: valor em peso normal, não 600.
   *
   * Usado nas datas de expiração e emissão, que competiam com o VENCIMENTO ao
   * lado — a data que de fato decide a cobrança. Elas continuam legíveis e no
   * mesmo corpo; o que muda é quem ganha o olhar primeiro.
   */
  secundaria?: boolean
}

/**
 * Uma informação da grade: rótulo pequeno em caixa alta, valor abaixo.
 *
 * O layout antigo gastava a largura inteira do modal em cada campo para
 * carregar um único dado, com um pontilhado ligando rótulo e valor. Onze campos
 * viravam onze linhas de rolagem; empilhar rótulo e valor deixa cada um ocupar
 * só o que precisa e libera a grade para reflui-los lado a lado.
 */
export const Celula = ({ label, children, larga, secundaria }: CelulaProps) => (
  <Stack
    gap="2px"
    minWidth={0}
    sx={larga ? { gridColumn: '1 / -1' } : undefined}
  >
    <Typography
      sx={{
        fontSize: '11px',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.neutral[400],
        lineHeight: 1.4
      }}
    >
      {label}
    </Typography>
    {typeof children === 'string' ? (
      <Typography
        variant={secundaria ? 'smLight' : 'smBold'}
        sx={{
          fontSize: '14px',
          color: secundaria ? theme.palette.neutral[300] : undefined
        }}
      >
        {children}
      </Typography>
    ) : (
      children
    )}
  </Stack>
)

/**
 * Valor de um campo desligado.
 *
 * Quatro dos onze campos costumam dizer "Não". Mantê-los na grade preserva a
 * resposta a "isso está configurado?", mas em cinza eles param de disputar
 * atenção com o que de fato está ativo.
 */
export const NaoConfigurado = () => (
  <Typography
    variant="smBold"
    sx={{ fontSize: '14px', color: theme.palette.neutral[500] }}
  >
    Não
  </Typography>
)

/** Lista de chips que quebra em várias linhas sem estourar a largura. */
export const ListaChips = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '6px',
      marginTop: '3px',
      // Um chip espremido corta o próprio rótulo ("RECEBIMENTO MA..."), e um
      // meio de pagamento pela metade não identifica nada. Sem encolher, o
      // chip inteiro desce para a linha seguinte.
      '& > *': { flexShrink: 0, maxWidth: '100%' }
    }}
  >
    {children}
  </Box>
)

/** Iniciais para o avatar do pagador. */
const iniciais = (nome: string) =>
  nome
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((parte) => parte[0] ?? '')
    .join('')
    .toUpperCase()

/**
 * Descrição com duas linhas e "Ver mais".
 *
 * O campo aceita até 2000 caracteres, e um contrato inteiro sozinho vale por
 * metade do cabeçalho — empurrava todo o resto para fora da primeira tela.
 * Textos curtos não ganham o botão.
 */
export const DescricaoRecolhivel = ({ texto }: { texto: string }) => {
  const [aberta, setAberta] = React.useState(false)
  // ~2 linhas na largura do modal; abaixo disso recolher não ajuda em nada.
  const longa = texto.length > 180

  return (
    <Stack gap="2px" minWidth={0}>
      <Typography
        sx={{
          whiteSpace: 'pre-line',
          fontSize: '13px',
          ...(longa && !aberta
            ? {
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }
            : {})
        }}
      >
        {texto}
      </Typography>

      {longa && (
        <Typography
          component="button"
          type="button"
          onClick={() => setAberta((v) => !v)}
          sx={{
            alignSelf: 'flex-start',
            background: 'none',
            border: 0,
            padding: '2px 0',
            cursor: 'pointer',
            font: 'inherit',
            fontSize: '12px',
            fontWeight: 600,
            color: theme.palette.primary[100]
          }}
        >
          {aberta ? 'Ver menos' : 'Ver mais'}
        </Typography>
      )}
    </Stack>
  )
}

type PagadorProps = {
  nome: string
  documento?: string
  email?: string
  telefone?: string
  /** Link para a ficha do cliente. Sem ele o nome vira texto simples. */
  href?: string
  /**
   * Botões alinhados à direita, na mesma linha do nome. No detalhe da cobrança
   * são "Reenviar notificação" e "Visualizar fatura": ocupavam uma faixa só
   * deles acima do resumo, gastando uma linha inteira do modal.
   */
  acoes?: React.ReactNode
}

/**
 * Faixa do pagador, de largura inteira, acima do resto.
 *
 * Ficar em faixa e não em cartão estreito é o que resolve o nome longo: razão
 * social de 60+ caracteres cabe sem truncar, e o layout não muda de forma entre
 * um cliente e outro.
 */
export const PagadorFaixa = ({
  nome,
  documento,
  email,
  telefone,
  href,
  acoes
}: PagadorProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      flexWrap: 'wrap',
      paddingBottom: '14px',
      borderBottom: `1px solid ${theme.palette.neutral[700]}`,
      minWidth: 0
    }}
  >
    {/* Contorno em vez de disco colorido, como o CustomerAvatar do detalhe do
        cliente: o avatar identifica, não é um destaque a mais disputando
        atenção com o valor da cobrança logo abaixo. */}
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        flex: 'none',
        boxSizing: 'border-box',
        border: `1px solid ${theme.palette.neutral[400]}`,
        background: 'transparent',
        color: theme.palette.neutral[400],
        display: 'grid',
        placeContent: 'center',
        fontSize: '13px',
        fontWeight: 600,
        textTransform: 'uppercase'
      }}
    >
      {iniciais(nome)}
    </Box>

    <Stack minWidth={0}>
      {href ? (
        <Tooltip title="Abrir cliente em nova guia">
          <Typography
            variant="smBold"
            component="a"
            href={href}
            target="_blank"
            rel="noreferrer"
            sx={{
              fontSize: '16px',
              color: 'inherit',
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              width: 'fit-content',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {nome}
            {/* Sem o ícone o nome parecia texto comum: nada indicava que dava
                para abrir a ficha do cliente. */}
            <Icon
              icon="open"
              sx={{ fontSize: '15px' }}
              colorSX={theme.palette.neutral[400]}
            />
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="smBold" sx={{ fontSize: '16px' }}>
          {nome}
        </Typography>
      )}
      {/* Documento e contato na mesma linha, logo abaixo do nome: jogados
          contra a borda direita, ficavam a meia tela de distância do cliente a
          que pertencem. Mesmo arranjo do cabeçalho do detalhe do cliente. */}
      {(documento || email || telefone) && (
        <Typography
          sx={{
            fontSize: '12.5px',
            color: theme.palette.neutral[400],
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {[documento, email, telefone].filter(Boolean).join(' · ')}
        </Typography>
      )}
    </Stack>

    {acoes && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginLeft: 'auto',
          flexWrap: 'wrap'
        }}
      >
        {acoes}
      </Box>
    )}
  </Box>
)

type DetailSummaryProps = {
  /** Valor por ciclo — texto ou o componente de cupom. */
  valor: React.ReactNode
  /** Explicação do valor, exibida num (i) ao lado do rótulo "Valor". */
  valorTooltip?: string
  /** Linha de apoio do cartão: "Mensal · vence dia 12". */
  cadencia: string
  /** Chips do cartão (bonificação, ciclo atual). */
  destaques?: React.ReactNode
  /** A faixa do pagador, renderizada acima de tudo. */
  pagador?: React.ReactNode
  /** Demais informações, já como <Celula>. */
  children: React.ReactNode
}

/**
 * Cabeçalho dos modais de detalhe (recorrência e cobrança).
 *
 * O pagador ocupa a faixa superior; abaixo, o cartão do valor à esquerda e as
 * demais informações numa grade que se reflui conforme a largura. O merchant
 * abre estes modais atrás de três coisas — de quem, quanto e quando — e as três
 * ficam visíveis sem rolagem.
 */
const DetailSummary = ({
  valor,
  valorTooltip,
  cadencia,
  destaques,
  pagador,
  children
}: DetailSummaryProps) => (
  <Stack gap="14px">
    {pagador}

    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '200px 1fr',
        gap: '18px',
        [theme.breakpoints.down(760)]: {
          gridTemplateColumns: '1fr'
        }
      }}
    >
      <Stack
        gap="4px"
        alignSelf="start"
        sx={{
          // Sem caixa: o valor já se destaca pelo tamanho, e um container
          // cinza dentro de um accordion branco brigava com os vizinhos.
          paddingRight: '20px',
          borderRight: `1px solid ${theme.palette.neutral[700]}`,
          minWidth: 0,
          [theme.breakpoints.down(760)]: {
            paddingRight: 0,
            borderRight: 'none',
            paddingBottom: '14px',
            borderBottom: `1px solid ${theme.palette.neutral[700]}`
          }
        }}
      >
        <Stack direction="row" alignItems="center" gap="4px">
          <Typography
            sx={{
              fontSize: '11px',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: theme.palette.neutral[400]
            }}
          >
            Valor
          </Typography>
          {valorTooltip && <InfoHint title={valorTooltip} compact />}
        </Stack>

        {typeof valor === 'string' ? (
          <Typography
            sx={{
              fontSize: '24px',
              fontWeight: 600,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {valor}
          </Typography>
        ) : (
          valor
        )}

        {/* SC-10890: a cadência carrega o VENCIMENTO ("Vence em 04/09/2026"),
            que é a data que decide a cobrança — se já venceu, há quantos dias,
            se vence hoje. Em 12,5px e cinza claro ela pesava MENOS que "data de
            expiração" e "data de emissão" ao lado, que são datas de apoio
            renderizadas em <Celula> (14px, smBold, tinta cheia).

            Aqui ela sobe para o mesmo corpo das Celulas, com peso 600 e a tinta
            de texto, ficando acima delas na hierarquia — como manda a leitura de
            quem cobra. */}
        <Typography
          sx={{
            fontSize: '14px',
            fontWeight: 600,
            color: theme.palette.neutral[100],
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {cadencia}
        </Typography>

        {destaques && (
          <Box
            sx={{
              marginTop: '8px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px'
            }}
          >
            {destaques}
          </Box>
        )}
      </Stack>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '14px 20px',
          alignContent: 'start'
        }}
      >
        {children}
      </Box>
    </Box>
  </Stack>
)

export default DetailSummary
